import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";

const Retail = lazy(() => import("./pages/Retail"));
const Industrial = lazy(() => import("./pages/Industrial"));
const Medical = lazy(() => import("./pages/Medical"));
const Compania = lazy(() => import("./pages/Compania"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Privacidad = lazy(() => import("./pages/Privacidad"));
const Legal = lazy(() => import("./pages/Legal"));
const CanalEtico = lazy(() => import("./pages/CanalEtico"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gracias = lazy(() => import("./pages/Gracias"));
const RetailVenue = lazy(() => import("./pages/RetailVenue"));
const RetailCalculator = lazy(() => import("./pages/RetailCalculator"));
const IndustrialSector = lazy(() => import("./pages/IndustrialSector"));
const EstudioImplantacion = lazy(() => import("./pages/EstudioImplantacion"));
const ContentIndex = lazy(() => import("./pages/ContentIndex"));
const ContentPiece = lazy(() => import("./pages/ContentPiece"));

const queryClient = new QueryClient();

export const SUPPORTED_LANGS = ["es", "en", "it", "de", "fr"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const isLang = (value?: string): value is SupportedLang =>
  !!value && (SUPPORTED_LANGS as readonly string[]).includes(value);

/** Redirects any non-prefixed URL to the current language prefix */
const LangRedirect = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = isLang(i18n.language?.substring(0, 2))
    ? i18n.language.substring(0, 2)
    : "es";
  const path = location.pathname === "/" ? "" : location.pathname;
  return <Navigate to={`/${lang}${path}${location.search}${location.hash}`} replace />;
};

/** Keeps the URL prefix and the active language in sync */
const LangShell = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  const navigate = useNavigate();

  // URL -> idioma
  useEffect(() => {
    if (!isLang(lang)) return;
    document.documentElement.lang = lang;
    if (i18n.language?.substring(0, 2) !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Idioma (selector / geolocalización) -> URL
  useEffect(() => {
    const onChange = (lng: string) => {
      const next = lng?.substring(0, 2);
      if (!isLang(next) || next === lang) return;
      const rest = location.pathname.replace(/^\/[^/]+/, "");
      navigate(`/${next}${rest}${location.search}${location.hash}`, { replace: true });
    };
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, [i18n, lang, location, navigate]);

  if (!isLang(lang)) {
    return <LangRedirect />;
  }


  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="retail" element={<Retail />} />
        <Route path="retail/calculadora" element={<RetailCalculator />} />
        <Route path="retail/:venue" element={<RetailVenue />} />
        <Route path="industrial" element={<Industrial />} />
        <Route path="industrial/:sector" element={<IndustrialSector />} />
        <Route path="estudio-implantacion" element={<EstudioImplantacion />} />
        <Route path="recursos" element={<ContentIndex kind="recurso" />} />
        <Route path="recursos/:slug" element={<ContentPiece kind="recurso" />} />
        <Route path="casos" element={<ContentIndex kind="caso" />} />
        <Route path="casos/:slug" element={<ContentPiece kind="caso" />} />
        <Route path="medical" element={<Medical />} />
        <Route path="compania" element={<Compania />} />
        <Route path="cookies" element={<Cookies />} />
        <Route path="privacidad" element={<Privacidad />} />
        <Route path="legal" element={<Legal />} />
        <Route path="canal-etico" element={<CanalEtico />} />
        <Route path="gracias/:form" element={<Gracias />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/** Router-agnostic app tree: wrapped by BrowserRouter (client) or StaticRouter (prerender). */
export const AppTree = ({ Router }: { Router: (p: { children: React.ReactNode }) => JSX.Element }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/:lang/*" element={<LangShell />} />
            {/* ADD ALL CUSTOM ROUTES INSIDE LangShell */}
            <Route path="*" element={<LangRedirect />} />
          </Routes>
        </Suspense>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

const ClientRouter = ({ children }: { children: React.ReactNode }) => <BrowserRouter>{children}</BrowserRouter>;

const App = () => <AppTree Router={ClientRouter} />;

export default App;
